import {
  FunctionComponent,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { Select, SelectProps } from "antd";
import debounce from "lodash.debounce";
import { Loader } from "@ui-kit/Loader/Loader";
import { translate } from "@lib/translate";
import { SearchIcon } from "@icons/SearchIcon";
import { colors, ConfigProvider } from "@ui-kit/ConfigProvider/ConfigProvider";
import { CloseInCircleIcon } from "@icons/CloseInCircleIcon";

import "./Search.css";

interface SearchOption<T> {
  key: string;
  label: string;
  value: string;
  data: T;
}

interface SearchProps<T> {
  placeholder?: string;
  onSelect: (value: T) => void;
  onSearchChange: (value: string) => Promise<T[]>;
  optionRender: (option: SearchOption<T>) => ReactNode;
  width?: string;
  dropdownWidth?: string;
  prefix?: ReactNode;
  variant?: "default" | "rounded";
  autoFocus?: boolean;
  size?: "small" | "middle" | "large";
}

const DEBOUNCE_TIMEOUT = 500;
const SEARCH_PLACEHOLDER = translate("common.search") ?? "Search";
const WIDTH = "221px";
const DROPDOWN_WIDTH = "296px";

// Require at least n characters before search is performed
const isSearchEmpty = (value: string | undefined) => {
  return !value || value.length < 2;
};

const Search: FunctionComponent<SearchProps<any>> = ({
  placeholder = SEARCH_PLACEHOLDER,
  onSearchChange,
  optionRender,
  onSelect,
  width = WIDTH,
  dropdownWidth = DROPDOWN_WIDTH,
  prefix = <SearchIcon size={20} color={colors.white} />,
  variant = "default",
  autoFocus = false,
  size = "middle",
}) => {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<SelectProps["options"]>([]);
  const [value, setValue] = useState<string>();
  const fetchRef = useRef(0);

  const handleChange = useCallback(
    (newValue: string) => {
      const option = options?.find((option) => option.value === newValue);
      if (option) {
        onSelect(option);
      }
      setValue("");
    },
    [onSelect, options],
  );

  const debounceFetcher = useMemo(() => {
    const loadOptions = async (value: string) => {
      setValue(value);
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);

      const newOptions = isSearchEmpty(value)
        ? []
        : await onSearchChange(value);
      if (fetchId !== fetchRef.current) {
        // for fetch callback order
        return;
      }

      setOptions(newOptions);
      setFetching(false);
    };

    return debounce(loadOptions, DEBOUNCE_TIMEOUT);
  }, [onSearchChange]);

  return (
    <ConfigProvider
      themeConfigOverride={
        variant === "rounded"
          ? {
              components: {
                Select: {
                  selectorBg: "rgba(0, 0, 0, 0.2)",
                  colorFillTertiary: "rgba(0, 0, 0, 0.2)",
                  colorFillSecondary: "rgba(0, 0, 0, 0.2)",
                  colorTextPlaceholder: "white",
                },
              },
            }
          : {
              components: {
                Select: {
                  selectorBg: "#FFF",
                  colorFillTertiary: "#FFF",
                  colorFillSecondary: "#FFF",
                  controlOutline: "black",
                },
              },
            }
      }>
      <Select
        showSearch
        className={`ui-kit-search ${variant}`}
        allowClear={{
          clearIcon: <CloseInCircleIcon size={20} color={colors.white} />,
        }}
        autoFocus={autoFocus}
        value={null}
        variant={variant === "rounded" ? "filled" : undefined}
        placeholder={placeholder}
        style={{ width: width }}
        dropdownStyle={{ width: dropdownWidth }}
        defaultActiveFirstOption={false}
        suffixIcon={null}
        size={size}
        prefix={prefix}
        filterOption={false}
        notFoundContent={
          fetching ? (
            <Loader size="small" />
          ) : isSearchEmpty(value) ? null : (
            <div>{translate("common.noResults")}</div>
          )
        }
        onSearch={debounceFetcher}
        onChange={handleChange}
        optionRender={optionRender as any}
        options={options}
      />
    </ConfigProvider>
  );
};

export { Search };
